import { Button } from '@audio-network/componentlibrary'
import { Container } from '@audio-network/componentlibrary'
import {
	Discoball,
	Film,
	Instrument,
	Mix,
	Sliders,
	Smiley,
	Toolkit,
} from '@audio-network/componentlibrary/icons'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import React from 'react'

import sessionStore from '@/stores/sessionStore'
import { hasStemsAccess } from '@/utils/hasStemsAccess'

import AdvancedFilterTabs from './AdvancedFilterTabs/AdvancedFilterTabs'
import curatedFacets from './curatedFacets'
import Duration from './Duration/Duration'
import FilterButton from './FilterButton/FilterButton'
import { useDropdowns } from './Filters.hooks'
import styles from './Filters.module.css'
import { FiltersProps } from './Filters.types'
import InstrumentalFilter from './InstrumentalFilter/InstrumentalFilter'
import KeyFilter from './KeyFilter/KeyFilter'
import MixesAndStems from './MixesAndStems/MixesAndStems'
import ProFilter from './ProFilter/ProFilter'
import SimpleFilter from './SimpleFilter/SimpleFilter'
import TempoBpm from './TempoBpm/TempoBpm'
import VocalAndLyricsFilter from './VocalAndLyricsFilter/VocalAndLyricsFilter'

const Filters = ({ locale }: FiltersProps): JSX.Element => {
	const { t } = useTranslation(['search'])

	const user = sessionStore((state) => state.session?.user)

	const {
		activeDropdown,
		showMobileFilters,
		toggleDropdown,
		toggleMobileFilters,
	} = useDropdowns()

	const attributeLocale = locale.startsWith('en')
		? ''
		: `_${locale.charAt(3)}${locale.charAt(4).toLowerCase()}`

	const localisedCuratedFacets = curatedFacets(attributeLocale)

	const dropdowns = [
		{
			attribute: `MusicalStyles${attributeLocale}`,
			Component: (
				<SimpleFilter
					attribute={`MusicalStyles${attributeLocale}`}
					curatedFacets={
						localisedCuratedFacets[`MusicalStyles${attributeLocale}`]
					}
					data-testid={`MusicalStyles${attributeLocale}`}
				/>
			),
			Icon: <Discoball />,
			name: t('musical-styles'),
			primary: true,
		},
		{
			attribute: `Moods${attributeLocale}`,
			Component: (
				<SimpleFilter
					attribute={`Moods${attributeLocale}`}
					curatedFacets={localisedCuratedFacets[`Moods${attributeLocale}`]}
					data-testid={`Moods${attributeLocale}`}
				/>
			),
			Icon: <Smiley />,
			name: t('moods'),
			primary: true,
		},
		{
			attribute: `Instrumentations${attributeLocale}`,
			Component: (
				<SimpleFilter
					attribute={`Instrumentations${attributeLocale}`}
					curatedFacets={
						localisedCuratedFacets[`Instrumentations${attributeLocale}`]
					}
					data-testid={`Instrumentations${attributeLocale}`}
				/>
			),
			Icon: <Instrument />,
			name: t('instrumentations'),
			primary: true,
		},
		{
			attribute: `ProductionGenres${attributeLocale}`,
			Component: (
				<SimpleFilter
					attribute={`ProductionGenres${attributeLocale}`}
					curatedFacets={
						localisedCuratedFacets[`ProductionGenres${attributeLocale}`]
					}
					data-testid={`ProductionGenres${attributeLocale}`}
				/>
			),
			Icon: <Film />,
			name: t('production-genres'),
			primary: true,
		},
		{
			attribute: `Toolkit${attributeLocale}`,
			Component: (
				<SimpleFilter
					attribute={`Toolkit${attributeLocale}`}
					curatedFacets={localisedCuratedFacets[`Toolkit${attributeLocale}`]}
					data-testid={`Toolkit${attributeLocale}`}
				/>
			),
			Icon: <Toolkit />,
			name: t('toolkit'),
			primary: true,
		},
		{
			attribute: 'MixesAndStems',
			Component: <MixesAndStems />,
			Icon: <Mix />,
			name: hasStemsAccess(user) ? t('mixes-and-stems') : t('mixes'),
			secondary: true,
		},
		{
			attribute: 'AdvancedFilters',
			Component: <AdvancedFilterTabs />,
			Icon: <Sliders />,
			name: t('advanced-filters'),
			secondary: true,
		},
		{
			attribute: 'Duration',
			Component: <Duration />,
			name: t('duration'),
			advanced: true,
		},
		{
			attribute: 'TempoBpm',
			Component: <TempoBpm />,
			name: t('tempo-bpm'),
			advanced: true,
		},
		{
			attribute: 'Pro',
			Component: <ProFilter />,
			name: t('pro'),
			advanced: true,
		},
		{
			attribute: 'KeyMetre',
			Component: <KeyFilter />,
			name: t('key-metre'),
			advanced: true,
		},
		{
			attribute: 'Lyrics',
			Component: <VocalAndLyricsFilter />,
			name: t('lyrics'),
			advanced: true,
		},
	]

	return (
		<Container className={styles.container} maxWidth="large">
			<div
				className={styles.filters}
				data-search-filters
				data-search-open-filter={activeDropdown}
			>
				<FilterButton
					data-testid="filters-mobile-button"
					aria-haspopup
					aria-expanded={showMobileFilters}
					className={styles.filtersMobileButton}
					mobile
					onClick={toggleMobileFilters}
					title={t('filters')}
					Icon={<Sliders />}
				/>

				<div
					aria-hidden={!showMobileFilters}
					className={styles.filtersMobileBackdrop}
				/>
				<div
					className={clsx(
						styles.dropdownWrapper,
						showMobileFilters && styles.showMobileFilters
					)}
					data-dropdowns
					data-title={t('filters')}
					data-testid="filters-pop-up"
				>
					{/* Mobile only buttons to enable horizontal scrolling */}
					<div
						className={styles.mobileButtons}
						data-testid="filters-mobile-buttons"
					>
						{dropdowns.map((dropdown) => {
							if (dropdown.attribute === 'AdvancedFilters') {
								return
							}

							const expanded = activeDropdown === dropdown.attribute

							return (
								<FilterButton
									aria-haspopup
									aria-expanded={expanded}
									data-testid={`${dropdown.attribute}-button`}
									key={dropdown.attribute}
									onClick={(event) => toggleDropdown(event, dropdown.attribute)}
									mobile
									title={dropdown.name}
								/>
							)
						})}
					</div>
					{dropdowns.map((dropdown) => {
						const expanded = activeDropdown === dropdown.attribute

						return (
							<div
								className={styles.dropdown}
								key={dropdown.attribute}
								data-testid={`${dropdown.attribute}-dropdown`}
							>
								<FilterButton
									aria-haspopup
									aria-expanded={expanded}
									className={clsx(
										styles.mainFilterButton,
										dropdown.advanced && styles.hideOnDesktop
									)}
									onClick={(event) => toggleDropdown(event, dropdown.attribute)}
									Icon={dropdown.Icon}
									primary={dropdown.primary}
									secondary={dropdown.secondary}
									title={dropdown.name}
								/>

								<div
									aria-hidden={!expanded}
									data-dropdown={dropdown.attribute}
									className={clsx(
										styles.dropdownPanel,
										dropdown.attribute === 'AdvancedFilters' && styles.advanced
									)}
								>
									{dropdown.Component}
								</div>
							</div>
						)
					})}
					<div className={styles.closeMobileFilters}>
						<Button
							className={styles.closeMobileFiltersButton}
							onClick={toggleMobileFilters}
						>
							{t('close')}
						</Button>
					</div>
				</div>
				<div className={styles.instrumentalFilterWrapper}>
					<InstrumentalFilter />
				</div>
			</div>
		</Container>
	)
}

export default Filters
